/**
 * Loading and Mapping methods for LoaderPreProcess
 * These methods are attached to LoaderPreProcess.prototype in the main file
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { PreProcessTemplateEntry, PreprocessedTemplate, ReplacementMapping, ReplacementType } from '../model/index.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';
import type { IConfig } from '../../config/iConfig.js';

/**
 * Mapping methods to be attached to LoaderPreProcess prototype
 */
export const mappingMethods = {
    /**
     * Creates template structure replacement mappings
     */
    _createAllReplacementMappingsWithCache(this: LoaderPreProcess, templates: Map<string, PreProcessTemplateEntry>, appSite: string): Map<string, PreProcessTemplateEntry> {
        // Extract templates from ALL cached AppSites
        const templateDict = new Map<string, PreprocessedTemplate>();
        for (const [, cachedAppSiteValue] of this._store.getAllAppSites().entries()) {
            for (const [key, { template }] of cachedAppSiteValue.entries()) {
                templateDict.set(key.toLowerCase(), template);
            }
        }

        Logger.debug(`CreateAllReplacementMappingsWithCache: Using ${templateDict.size} total templates`, 'LoaderPreProcess');

        for (const [_, { template }] of templates.entries()) {
            template.replacementMappings = [];
        }

        // Phase 1: Simple placeholder mappings
        for (const [key, { template }] of templates.entries()) {
            const templateName = key.substring(key.indexOf('_') + 1);
            (this.constructor as typeof LoaderPreProcess)._createPlaceholderReplacementMappings(template, templateName, templateDict, appSite, this._config!);
        }

        // Phase 2: Slotted template mappings
        for (const [_, { template }] of templates.entries()) {
            (this.constructor as typeof LoaderPreProcess)._createSlottedTemplateReplacementMappings(template, templateDict, appSite, this._config!);
        }

        let totalMappings = 0;
        for (const [_, { template }] of templates.entries()) {
            totalMappings += template.replacementMappings.length;
        }
        Logger.info(`Total replacement mappings created for ${appSite}: ${totalMappings}`, 'LoaderPreProcess');

        return templates;
    }
};

/**
 * Static methods to be attached to LoaderPreProcess class
 */
export const mappingStaticMethods = {
    /**
     * Creates replacement mappings for simple placeholders
     */
    _createPlaceholderReplacementMappings(template: PreprocessedTemplate, currentTemplateName: string, allTemplates: Map<string, PreprocessedTemplate>, appSite: string, config: IConfig): void {
        if (!template.hasPlaceholders) return;

        for (const placeholder of template.placeholders) {
            // Handle skip markers
            if (placeholder.name.startsWith('!')) {
                Logger.debug(`Creating skip replacement mapping for placeholder: ${placeholder.fullMatch}`, 'LoaderPreProcess');
                const mapping = new ReplacementMapping();
                mapping.originalText = placeholder.fullMatch;
                mapping.replacementText = config.defaultSkipHtml;
                mapping.type = ReplacementType.SimpleTemplate;
                mapping.targetTemplateName = placeholder.name;
                template.replacementMappings.push(mapping);
                continue;
            }

            const { baseTemplateName, isReusePattern } = CommonUtil.parseComponentReusePattern(placeholder.templateKey, currentTemplateName);

            if (isReusePattern) {
                Logger.debug(`CreatePlaceholderReplacementMappings: Component reuse detected - Original: '${placeholder.templateKey}', Base: '${baseTemplateName}'`, 'LoaderPreProcess');
            }

            // Try current appSite
            const targetTemplateKey = `${appSite.toLowerCase()}_${baseTemplateName}`;
            let targetTemplate = allTemplates.get(targetTemplateKey.toLowerCase());

            if (!targetTemplate) {
                // Search in all loaded templates
                const searchKey = `_${baseTemplateName}`;
                for (const key of allTemplates.keys()) {
                    if (key.toLowerCase().endsWith(searchKey.toLowerCase())) {
                        targetTemplate = allTemplates.get(key);
                        Logger.debug(`Template '${baseTemplateName}' not found as '${targetTemplateKey}', using fallback from '${key}'`, 'LoaderPreProcess');
                        break;
                    }
                }
            }

            if (targetTemplate) {
                const processedTemplate = targetTemplate.originalContent;
                Logger.debug(`Creating replacement mapping: ${placeholder.fullMatch} -> ${placeholder.templateKey}`, 'LoaderPreProcess');
                const mapping = new ReplacementMapping();
                mapping.originalText = placeholder.fullMatch;
                mapping.replacementText = processedTemplate;
                mapping.type = ReplacementType.SimpleTemplate;
                mapping.targetTemplateName = placeholder.templateKey;
                template.replacementMappings.push(mapping);
            } else {
                Logger.debug(`Creating fallback placeholder mapping for ${placeholder.fullMatch}`, 'LoaderPreProcess');
                const mapping = new ReplacementMapping();
                mapping.originalText = placeholder.fullMatch;
                mapping.replacementText = placeholder.fullMatch;
                mapping.type = ReplacementType.SimpleTemplate;
                mapping.targetTemplateName = placeholder.templateKey;
                template.replacementMappings.push(mapping);
            }
        }
    },

    /**
     * Creates replacement mappings for slotted templates
     */
    _createSlottedTemplateReplacementMappings(template: PreprocessedTemplate, allTemplates: Map<string, PreprocessedTemplate>, appSite: string, config: IConfig): void {
        if (!template.hasSlottedTemplates) return;

        Logger.debug(`[CREATE-SLOTMAP] Called for template with ${template.slottedTemplates.length} slotted templates, appSite=${appSite}`, 'LoaderPreProcess');

        for (const slottedTemplate of template.slottedTemplates) {
            // Handle skip markers
            if (slottedTemplate.name.startsWith('!')) {
                Logger.debug(`[CREATE-SLOTMAP] Creating skip replacement mapping for slotted template: ${slottedTemplate.fullMatch}`, 'LoaderPreProcess');
                const mapping = new ReplacementMapping();
                mapping.originalText = slottedTemplate.fullMatch;
                mapping.replacementText = config.defaultSkipHtml;
                mapping.type = ReplacementType.SlottedTemplate;
                mapping.targetTemplateName = slottedTemplate.name;
                template.replacementMappings.push(mapping);
                continue;
            }

            const fullMatch = slottedTemplate.fullMatch;

            // Try current appSite
            const targetTemplateKey = `${appSite.toLowerCase()}_${slottedTemplate.templateKey}`;
            let targetTemplate = allTemplates.get(targetTemplateKey.toLowerCase());

            Logger.debug(`[CREATE-SLOTMAP] Looking for slotted template '${slottedTemplate.templateKey}' as '${targetTemplateKey}'`, 'LoaderPreProcess');

            if (!targetTemplate) {
                // Search in all loaded templates
                const searchKey = `_${slottedTemplate.templateKey}`;
                Logger.debug(`[CREATE-SLOTMAP] Primary key not found, searching with suffix '${searchKey}'`, 'LoaderPreProcess');
                for (const key of allTemplates.keys()) {
                    if (key.toLowerCase().endsWith(searchKey.toLowerCase())) {
                        targetTemplate = allTemplates.get(key);
                        Logger.debug(`[CREATE-SLOTMAP] Found with fallback: '${key}'`, 'LoaderPreProcess');
                        break;
                    }
                }
            }

            if (targetTemplate) {
                let processedTemplate = targetTemplate.originalContent;

                Logger.debug(`[CREATE-SLOTMAP] Target template content length: ${processedTemplate.length}`, 'LoaderPreProcess');

                // Process slots
                for (const slot of slottedTemplate.slots) {
                    Logger.debug(`[CREATE-SLOTMAP] Processing slot: SlotKey='${slot.slotKey}'`, 'LoaderPreProcess');

                    const processedSlotContent = this._processSlotContentForReplacementMapping(slot, allTemplates, appSite, config, 0);
                    processedTemplate = processedTemplate.replace(slot.slotKey, processedSlotContent);
                }

                // Handle default slot
                if (slottedTemplate.slots.length === 0) {
                    const actualInnerContent = slottedTemplate.innerContent;
                    if (actualInnerContent && actualInnerContent.trim()) {
                        const defaultSlotKey = '{{$HTMLPLACEHOLDER}}';
                        if (processedTemplate.includes(defaultSlotKey)) {
                            processedTemplate = processedTemplate.replace(defaultSlotKey, actualInnerContent.trim());
                        }
                    }
                }

                // Remove remaining slot placeholders
                processedTemplate = CommonUtil.removeRemainingSlotPlaceholders(processedTemplate);

                Logger.debug(`[CREATE-SLOTMAP] Creating replacement mapping: original length=${fullMatch.length}, replacement length=${processedTemplate.length}`, 'LoaderPreProcess');

                const mapping = new ReplacementMapping();
                mapping.originalText = fullMatch;
                mapping.replacementText = processedTemplate;
                mapping.type = ReplacementType.SlottedTemplate;
                mapping.targetTemplateName = slottedTemplate.templateKey;
                template.replacementMappings.push(mapping);
            } else {
                Logger.debug(`[CREATE-SLOTMAP] Target template NOT FOUND for '${slottedTemplate.templateKey}', creating fallback mapping`, 'LoaderPreProcess');

                const mapping = new ReplacementMapping();
                mapping.originalText = slottedTemplate.fullMatch;
                mapping.replacementText = slottedTemplate.fullMatch;
                mapping.type = ReplacementType.SlottedTemplate;
                mapping.targetTemplateName = slottedTemplate.templateKey;
                template.replacementMappings.push(mapping);
            }
        }
    },

    /**
     * Processes slot content for creating replacement mappings
     */
    _processSlotContentForReplacementMapping(slot: PreprocessedTemplate['slottedTemplates'][number]['slots'][number], allTemplates: Map<string, PreprocessedTemplate>, appSite: string, config: IConfig, depth: number = 0): string {
        if (depth > (config.maxRecursionDepth || 7)) {
            Logger.warn(`[ERROR] Maximum recursion depth exceeded in ProcessSlotContentForReplacementMapping`, 'LoaderPreProcess');
            return slot.content;
        }

        let result = slot.content;

        // Process nested slotted templates
        for (const nestedSlottedTemplate of slot.nestedSlottedTemplates || []) {
            if (nestedSlottedTemplate.name.startsWith('!')) {
                Logger.debug(`Skipping nested slotted template with skip marker: ${nestedSlottedTemplate.fullMatch}`, 'LoaderPreProcess');
                continue;
            }

            const targetTemplateKey = `${appSite.toLowerCase()}_${nestedSlottedTemplate.templateKey}`;
            let targetTemplate = allTemplates.get(targetTemplateKey.toLowerCase());

            if (!targetTemplate) {
                const searchKey = `_${nestedSlottedTemplate.templateKey}`;
                for (const key of allTemplates.keys()) {
                    if (key.toLowerCase().endsWith(searchKey.toLowerCase())) {
                        targetTemplate = allTemplates.get(key);
                        break;
                    }
                }
            }

            if (targetTemplate) {
                let processedTemplate = targetTemplate.originalContent;

                for (const nestedSlot of nestedSlottedTemplate.slots) {
                    const processedNestedSlotContent = this._processSlotContentForReplacementMapping(nestedSlot, allTemplates, appSite, config, depth + 1);
                    processedTemplate = processedTemplate.replace(nestedSlot.slotKey, processedNestedSlotContent);
                }

                processedTemplate = CommonUtil.removeRemainingSlotPlaceholders(processedTemplate);
                result = result.replace(nestedSlottedTemplate.fullMatch, processedTemplate);
            }
        }

        return result;
    }
};
